import { useEffect, useState } from 'react';
import type { FdrGrid as FdrGridData, FdrRow } from '@fantasy/types';
import { api, ApiRequestError } from '../api.ts';

/** At least 4, 6 and 10 per the ticket — 6 doubles as the server's own
 * default (see `FdrQuery` in apps/api/src/routes/fixtures.ts), so the first
 * fetch here and a plain `GET /api/fixtures/fdr` agree without this ever
 * sending the query param. */
const HORIZONS = [4, 6, 10] as const;
const DEFAULT_HORIZON = 6;

type SortKey = 'club' | 'difficulty';
type SortDir = 'asc' | 'desc';

/** Ascending sorts a club name A→Z and a difficulty easiest→hardest. A `null`
 * average (no fixtures at all in the window) always sorts last regardless of
 * direction — there's nothing to rank it against, so it never competes with
 * a real number for either end of the list. */
function sortRows(rows: FdrRow[], key: SortKey, dir: SortDir): FdrRow[] {
  const factor = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    if (key === 'club') return a.club.localeCompare(b.club) * factor;
    if (a.averageDifficulty === null && b.averageDifficulty === null) return 0;
    if (a.averageDifficulty === null) return 1;
    if (b.averageDifficulty === null) return -1;
    return (a.averageDifficulty - b.averageDifficulty) * factor;
  });
}

function ariaSortFor(column: SortKey, sortKey: SortKey, sortDir: SortDir): 'ascending' | 'descending' | undefined {
  if (column !== sortKey) return undefined;
  return sortDir === 'asc' ? 'ascending' : 'descending';
}

/**
 * Fixture-difficulty grid — clubs down the side, gameweeks across the top.
 * Reference data, not squad-specific: fetches on mount and shows regardless
 * of whether a squad has been loaded (see `App.tsx`).
 */
export function FdrGrid() {
  const [horizon, setHorizon] = useState<number>(DEFAULT_HORIZON);
  const [grid, setGrid] = useState<FdrGridData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('club');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .getFdr(horizon)
      .then(({ grid: fetched }) => {
        if (!cancelled) setGrid(fetched);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof ApiRequestError ? err.message : 'Could not load fixture difficulty.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [horizon]);

  function onSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  const rows = grid ? sortRows(grid.rows, sortKey, sortDir) : [];

  return (
    <section className="panel panel--accent-2" aria-labelledby="fdr-heading">
      <div className="panel-head">
        <div>
          <h3 id="fdr-heading">Fixture difficulty</h3>
          <p className="hint">Who's got the kind run, and who's walking into a wall.</p>
        </div>
        <div className="field field--grow">
          <label id="fdr-horizon-label">Gameweeks ahead</label>
          <div className="row row--flush" role="group" aria-labelledby="fdr-horizon-label">
            {HORIZONS.map((value) => (
              <button
                key={value}
                type="button"
                className="ghost small"
                aria-pressed={horizon === value}
                onClick={() => setHorizon(value)}
              >
                {value}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && (
        <p className="hint" aria-live="polite">
          Loading fixtures…
        </p>
      )}

      {!loading && error && (
        <ul className="notices notices--flush">
          <li className="error">{error}</li>
        </ul>
      )}

      {!loading && !error && grid && (
        <div className="scroll-x">
          <table className="fdr-table">
            <caption className="hint table-caption">
              1 is the easiest fixture, 5 the hardest — colour is decoration, the number is the fact.
            </caption>
            <thead>
              <tr>
                <th scope="col" aria-sort={ariaSortFor('club', sortKey, sortDir)}>
                  <button type="button" className="fdr-sort" onClick={() => onSort('club')}>
                    Club
                  </button>
                </th>
                {grid.gameweeks.map((gw) => (
                  <th scope="col" key={gw} className="num">
                    GW{gw}
                  </th>
                ))}
                <th scope="col" className="num" aria-sort={ariaSortFor('difficulty', sortKey, sortDir)}>
                  <button type="button" className="fdr-sort" onClick={() => onSort('difficulty')}>
                    Avg
                  </button>
                </th>
                <th scope="col" className="num">
                  Fixtures
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.club}>
                  <th scope="row">{row.club}</th>
                  {row.cells.map((cell) => (
                    <td key={cell.gameweek} className="fdr-cell">
                      {cell.fixtures.length === 0 ? (
                        <span className="sr-only">Blank gameweek</span>
                      ) : (
                        cell.fixtures.map((fixture, i) => (
                          <span key={i} className="fdr-fixture" data-difficulty={fixture.difficulty}>
                            {fixture.isHome ? fixture.opponent.toUpperCase() : fixture.opponent.toLowerCase()}{' '}
                            {fixture.difficulty}
                            <span className="sr-only">{fixture.isHome ? ' (home)' : ' (away)'}</span>
                          </span>
                        ))
                      )}
                    </td>
                  ))}
                  <td className="num">{row.averageDifficulty ?? '—'}</td>
                  <td className="num">{row.fixtureCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
